import { createWalletClient, http, type Address } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { arbitrum, arbitrumSepolia } from 'viem/chains';
import type { EncodedTx, SubmissionResult } from '../types.js';
import type { OstiumClientConfig } from '../config.js';
import type { SubmissionStrategy } from './index.js';
import { OstiumError, OstiumErrorCode } from '../errors.js';

function classifySubmitError(err: unknown): OstiumError {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();

  if (
    lower.includes('execution reverted') ||
    lower.includes('reverted with reason') ||
    lower.includes('contract function') ||
    lower.includes('revert')
  ) {
    return new OstiumError(`Contract reverted: ${msg}`, OstiumErrorCode.CONTRACT_ERROR, err);
  }

  if (
    lower.includes('fetch') ||
    lower.includes('etimedout') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('network') ||
    lower.includes('timeout') ||
    lower.includes('connection')
  ) {
    return new OstiumError(`Network error: ${msg}`, OstiumErrorCode.NETWORK_ERROR, err);
  }

  return new OstiumError(
    `Transaction submission failed: ${msg}`,
    OstiumErrorCode.SUBMISSION_FAILED,
    err,
  );
}

/**
 * Self-submission strategy — the signer's EOA pays gas.
 * Requires rpcUrl in the config.
 */
export class SelfSubmissionStrategy implements SubmissionStrategy {
  readonly effectiveSender: Address;
  private readonly send: (encoded: EncodedTx) => Promise<`0x${string}`>;

  constructor(config: OstiumClientConfig, testnet: boolean) {
    if (!config.privateKey) {
      throw new OstiumError(
        'privateKey is required for self-submission mode',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    if (!config.rpcUrl) {
      throw new OstiumError(
        'rpcUrl is required for self-submission mode',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    const chain = testnet ? arbitrumSepolia : arbitrum;
    const account = privateKeyToAccount(config.privateKey);
    const walletClient = createWalletClient({ account, chain, transport: http(config.rpcUrl) });
    this.effectiveSender = account.address;
    this.send = (encoded) =>
      walletClient.sendTransaction({ account, to: encoded.to, data: encoded.data, value: encoded.value });
  }

  async submit(encoded: EncodedTx): Promise<SubmissionResult> {
    try {
      const txHash = await this.send(encoded);
      return { txHash };
    } catch (err) {
      throw classifySubmitError(err);
    }
  }
}
